import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import IssueDetailModal from '../../components/IssueDetailModal';
import { Loader2, ClipboardCheck, Calendar, Flag, FolderOpen, Paperclip } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLE = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-slate-200 text-slate-600',
};
const STATUS_LABEL = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
const PRIORITY_STYLE = { high: 'text-red-600', medium: 'text-amber-600', low: 'text-slate-500' };
const FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resolved', label: 'Resolved' },
];

const isOverdue = (i) => i.dueDate && ['open', 'in_progress'].includes(i.status) && new Date(i.dueDate) < new Date(new Date().toDateString());

const MyIssues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [detailId, setDetailId] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchIssues = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/my-issues`);
      setIssues(res.data || []);
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);

  const visible = issues.filter((i) => {
    if (filter === 'all') return true;
    if (filter === 'active') return i.status === 'open' || i.status === 'in_progress';
    return i.status === filter;
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><ClipboardCheck className="w-7 h-7 text-amber-500" /> My Issues</h1>
          <p className="text-slate-600 mt-1">Everything assigned to you across all projects</p>
        </div>

        <div className="flex gap-2 flex-wrap" data-testid="my-issues-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-sm ${filter === f.key ? 'bg-amber-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
              data-testid={`my-issues-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400" data-testid="my-issues-empty">No issues assigned to you here.</div>
        ) : (
          <div className="space-y-2" data-testid="my-issues-list">
            {visible.map((i) => (
              <button
                key={i.id}
                onClick={() => setDetailId(i.id)}
                className={`w-full text-left bg-white rounded-lg border p-4 hover:shadow-md transition-shadow flex items-center justify-between gap-4 ${isOverdue(i) ? 'border-red-300' : 'border-slate-200'}`}
                data-testid="my-issue-row"
              >
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{i.title}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                    <span className="flex items-center gap-1"><FolderOpen className="w-3 h-3" />{i.projectName}</span>
                    <span className={`flex items-center gap-1 ${PRIORITY_STYLE[i.priority] || ''}`}><Flag className="w-3 h-3" />{i.priority || 'medium'}</span>
                    {i.dueDate && <span className={`flex items-center gap-1 ${isOverdue(i) ? 'text-red-600 font-semibold' : ''}`}><Calendar className="w-3 h-3" />{i.dueDate.slice(0, 10)}{isOverdue(i) ? ' · overdue' : ''}</span>}
                    {(i.attachments || []).length > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" />{i.attachments.length}</span>}
                    {(i.comments || []).length > 0 && <span>💬 {i.comments.length}</span>}
                  </div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${STATUS_STYLE[i.status]}`}>{STATUS_LABEL[i.status]}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <IssueDetailModal
        issueId={detailId}
        currentUser={currentUser}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        onChanged={() => fetchIssues()}
      />
    </Layout>
  );
};

export default MyIssues;
